import React, { useState } from "react";
import { billingService } from "../../../services/billingService";
import {
  CreditCardIcon,
  DocumentTextIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";

interface BillingPortalCardProps {
  organizationId: string;
  currentPeriodEnd?: string;
}

export const BillingPortalCard: React.FC<BillingPortalCardProps> = ({
  organizationId,
  currentPeriodEnd,
}) => {
  const [isLoading, setIsLoading] = useState(false);

  const [error, setError] = useState<string | null>(null);

  const handleOpenPortal = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const session = await billingService.createPortalSession(
        organizationId,
        window.location.href,
      );
      window.location.href = session.portal_url;
    } catch (error: any) {
      console.error("Failed to open billing portal:", error);

      // Check if this is a development environment issue
      if (error.response?.status === 400 || error.response?.status === 500) {
        setError(
          "Billing portal is not available in development mode. This feature works in production with proper Stripe configuration.",
        );
      } else {
        setError(
          "Failed to open billing portal. Please try again or contact support.",
        );
      }
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <h3 className="text-lg font-medium text-gray-900 mb-2">
        Billing & Payment Methods
      </h3>
      <p className="text-sm text-gray-600 mb-4">
        Manage payment methods, download invoices, and view billing history.
      </p>

      <div className="space-y-3 mb-4">
        <div className="flex items-center text-sm text-gray-600">
          <CreditCardIcon className="h-4 w-4 mr-2 text-gray-400" />
          Update payment methods
        </div>
        <div className="flex items-center text-sm text-gray-600">
          <DocumentTextIcon className="h-4 w-4 mr-2 text-gray-400" />
          Download invoices and receipts
        </div>
        {currentPeriodEnd && (
          <div className="flex items-center text-sm text-gray-600">
            <ArrowTopRightOnSquareIcon className="h-4 w-4 mr-2 text-gray-400" />
            Manage advanced billing settings
          </div>
        )}
      </div>

      {error && (
        <div className="mb-4 p-3 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-sm text-amber-800">{error}</p>
        </div>
      )}

      <button
        onClick={handleOpenPortal}
        disabled={isLoading}
        className="w-full flex justify-center items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50"
      >
        {isLoading ? "Opening..." : "Open Billing Portal"}
        <ArrowTopRightOnSquareIcon className="ml-2 h-4 w-4" />
      </button>
    </div>
  );
};
